// src/pages/Warehouses/AddCategory.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../../axios';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Box,
  Tooltip,
  Skeleton,
  Paper,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
  Alert,
  Fade,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CategoryIcon from '@mui/icons-material/Category';
import PrintIcon from '@mui/icons-material/Print';
import StoreIcon from '@mui/icons-material/Store';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

export default function AddCategory() {
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const companyId = parseInt(localStorage.getItem('companyId'), 10);

  const [formData, setFormData] = useState({
    nameAr: '',
    nameEn: '',
    printerName: '',
    branchId: 0,
    allBranches: false,
    isActive: true,
    showInPos: true,
  });
  const [branches, setBranches] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!companyId) return;

    Promise.all([
      axios.get(`/Branch/by-company/${companyId}`),
      axios.get(`/Category/printers`),
      axios.get(`/Category/by-company/${companyId}`)
    ])
      .then(([branchesRes, printersRes, catsRes]) => {
        setBranches(branchesRes.data);
        setPrinters(printersRes.data);
        setCategories(catsRes.data);

        if (!id && branchesRes.data.length) {
          setFormData(f => ({ ...f, branchId: branchesRes.data[0].id }));
        }
      })
      .catch(err => {
        console.error('خطأ أثناء جلب البيانات:', err);
        setErrorMessage('فشل في تحميل البيانات');
      })
      .finally(() => setLoadingData(false));
  }, [companyId, id]);

  // Fetch category details for editing
  useEffect(() => {
    if (!id) return;

    setLoadingData(true);
    axios.get(`/Category/${id}`)
      .then(res => {
        const data = res.data;
        setFormData({
          nameAr: data.nameAr,
          nameEn: data.nameEn || '',
          printerName: data.printerName || '',
          branchId: data.branchId || 0,
          allBranches: data.allBranches,
          isActive: data.isActive,
          showInPos: data.showInPos ?? true,
        });
      })
      .catch(err => {
        console.error(err);
        setErrorMessage("فشل في تحميل بيانات التصنيف");
      })
      .finally(() => setLoadingData(false));
  }, [id]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(f => ({
      ...f,
      [name]:
        type === 'checkbox'
          ? checked
          : name === 'branchId'
          ? parseInt(value, 10)
          : value
    }));
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`/Category/by-company/${companyId}`);
      setCategories(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoadingSubmit(true);
    setSuccessMessage('');
    setErrorMessage('');
    
    try {
      const payload = {
        nameAr: formData.nameAr,
        nameEn: formData.nameEn || formData.nameAr,
        printerName: formData.printerName,
        branchId: formData.allBranches ? 0 : formData.branchId,
        allBranches: formData.allBranches,
        companyId,
        isActive: formData.isActive,
        showInPos: formData.showInPos,
      };

      if (id) {
        // Update existing category
        await axios.put(`/Category/${id}`, payload);
        setSuccessMessage('✅ تم تحديث التصنيف بنجاح!');
      } else {
        // Create new category
        await axios.post(`/Category`, payload);
        setSuccessMessage('✅ تم إضافة التصنيف بنجاح!');
        // Reset form only on create
        setFormData({
          nameAr: '',
          nameEn: '',
          printerName: '',
          branchId: branches[0]?.id || 0,
          allBranches: false,
          isActive: true,
          showInPos: true,
        });
      }
      
      await fetchCategories();
      setTimeout(() => setSuccessMessage(''), 3000);
      
      if (id) {
         // Optionally navigate back or stay
         // navigate('/add-category'); 
      }

    } catch (err) {
      console.error(err);
      setErrorMessage(err.response?.data?.message || '❌ حدث خطأ أثناء الحفظ');
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('❗ هل تريد حذف هذا التصنيف؟')) return;
    try {
      await axios.delete(`/Category/${id}`);
      await fetchCategories();
      setSuccessMessage('✅ تم حذف التصنيف بنجاح!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch {
      setErrorMessage('❌ حدث خطأ أثناء الحذف');
    }
  };

  // عرض الكروت للجوال
  const MobileCategoryCard = ({ category, index }) => (
    <Card
      sx={{
        mb: 2,
        borderRadius: 2,
        boxShadow: 3,
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-2px)',
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
              {category.nameAr}
            </Typography>
            {category.nameEn && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {category.nameEn}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography
              variant="caption"
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                fontWeight: 'bold'
              }}
            >
              #{index + 1}
            </Typography>
            {category.isActive ? (
              <CheckCircleIcon color="success" />
            ) : (
              <CancelIcon color="error" />
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {category.printerName && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PrintIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  <strong>الطابعة:</strong> {category.printerName}
                </Typography>
              </Box>
            </Grid>
          )}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StoreIcon fontSize="small" color="action" />
              <Typography variant="body2">
                <strong>الفرع:</strong> {category.allBranches ? 'كل الفروع' : (category.branchName || '-')}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>

      <Box sx={{ px: 2, pb: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/edit-category/${category.id}`)}
          sx={{ borderRadius: 2 }}
        >
          تعديل
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => handleDelete(category.id)}
          sx={{ borderRadius: 2 }}
        >
          حذف
        </Button>
      </Box>
    </Card>
  );

  return (
    <Container
      maxWidth={false}
      sx={{
        mt: { xs: 2, sm: 3, md: 4 },
        px: { xs: 2, sm: 3, md: 4 },
        pb: { xs: 3, sm: 4, md: 5 }
      }}
      dir="rtl"
    >
      {/* Header */}
      <Paper
        elevation={3}
        sx={{
          background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          mb: { xs: 3, sm: 4 },
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 3, md: 3.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CategoryIcon sx={{ fontSize: { xs: 32, sm: 40, md: 48 }, color: 'white' }} />
            <Typography
              variant={isMobile ? "h5" : "h4"}
              fontWeight="bold"
              color="white"
              sx={{
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                textShadow: '0 2px 4px rgba(0,0,0,0.2)',
              }}
            >
              🏷️ إدارة التصنيفات
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* رسائل النجاح والخطأ */}
      {successMessage && (
        <Fade in={!!successMessage}>
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        </Fade>
      )}
      {errorMessage && (
        <Fade in={!!errorMessage}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setErrorMessage('')}>
            {errorMessage}
          </Alert>
        </Fade>
      )}

      {/* نموذج الإضافة */}
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          mb: { xs: 3, sm: 4 },
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 3,
            pb: 1.5,
            borderBottom: '2px solid',
            borderColor: 'primary.main',
          }}
        >
          <SaveIcon color="primary" />
          {id ? 'تعديل التصنيف' : 'إضافة تصنيف جديد'}
        </Typography>

        {loadingData ? (
          <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
              {/* الاسم العربي */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="الاسم العربي *"
                  name="nameAr"
                  value={formData.nameAr}
                  onChange={handleChange}
                  required
                  helperText="يرجى إدخال الاسم بالعربية"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>

              {/* الاسم الإنجليزي */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="الاسم الإنجليزي"
                  name="nameEn"
                  value={formData.nameEn}
                  onChange={handleChange}
                  helperText="اتركه فارغاً لاستخدام الاسم العربي"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>

              {/* الطابعة */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>الطابعة</InputLabel>
                  <Select
                    name="printerName"
                    label="الطابعة"
                    value={formData.printerName}
                    onChange={handleChange}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="">بدون طابعة</MenuItem>
                    {printers.map(p => (
                      <MenuItem key={p} value={p}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PrintIcon fontSize="small" />
                          {p}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* الفرع */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth disabled={formData.allBranches}>
                  <InputLabel>الفرع</InputLabel>
                  <Select
                    name="branchId"
                    label="الفرع"
                    value={formData.branchId}
                    onChange={handleChange}
                    required={!formData.allBranches}
                    sx={{ borderRadius: 2 }}
                  >
                    {branches.map(b => (
                      <MenuItem key={b.id} value={b.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StoreIcon fontSize="small" />
                          {b.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* الخيارات */}
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%', justifyContent: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="allBranches"
                        checked={formData.allBranches}
                        onChange={handleChange}
                        color="primary"
                      />
                    }
                    label="كل الفروع"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleChange}
                        color="success"
                      />
                    }
                    label="نشط"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        name="showInPos"
                        checked={formData.showInPos}
                        onChange={handleChange}
                        color="warning"
                      />
                    }
                    label="إظهار في نقطة البيع"
                  />
                </Box>
              </Grid>

              {/* زر الحفظ */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate(-1)}
                    sx={{
                      borderRadius: 2,
                      px: 4,
                      py: 1.5,
                    }}
                  >
                    إلغاء
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loadingSubmit}
                    startIcon={<SaveIcon />}
                    sx={{
                      borderRadius: 2,
                      px: 4,
                      py: 1.5,
                      background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                      boxShadow: 3,
                      '&:hover': {
                        boxShadow: 6,
                        transform: 'translateY(-2px)',
                      }
                    }}
                  >
                    {loadingSubmit ? 'جاري الحفظ...' : (id ? '💾 تحديث التصنيف' : '💾 حفظ التصنيف')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* قائمة التصنيفات */}
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 3,
            pb: 1.5,
            borderBottom: '2px solid',
            borderColor: 'secondary.main',
          }}
        >
          <CategoryIcon color="secondary" />
          قائمة التصنيفات ({categories.length})
        </Typography>

        {loadingData ? (
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
        ) : isMobile ? (
          // عرض كروت للجوال
          <Box>
            {categories.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                <CategoryIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                <Typography color="text.secondary">
                  لا توجد تصنيفات حالياً
                </Typography>
              </Paper>
            ) : (
              categories.map((c, idx) => (
                <MobileCategoryCard key={c.id} category={c} index={idx} />
              ))
            )}
          </Box>
        ) : (
          // عرض جدول للشاشات الكبيرة
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  {['#', 'عربي', 'إنجليزي', 'طابعة', 'فرع', 'الحالة', 'إجراءات'].map((h, i) => (
                    <TableCell
                      key={i}
                      align="center"
                      sx={{
                        fontWeight: 'bold',
                        fontSize: { sm: '0.9rem', md: '1rem' },
                        py: 2,
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <CategoryIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                      <Typography color="text.secondary" variant="h6">
                        لا توجد تصنيفات حالياً
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  categories.map((c, idx) => (
                    <TableRow
                      key={c.id}
                      sx={{
                        '&:nth-of-type(odd)': { bgcolor: 'grey.50' },
                        '&:hover': { bgcolor: 'action.hover' },
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        {idx + 1}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {c.nameAr}
                      </TableCell>
                      <TableCell>{c.nameEn || '-'}</TableCell>
                      <TableCell align="center">
                        {c.printerName ? (
                          <Chip
                            icon={<PrintIcon />}
                            label={c.printerName}
                            size="small"
                            color="info"
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          icon={<StoreIcon />}
                          label={c.allBranches ? 'الكل' : c.branchName || '-'}
                          size="small"
                          color={c.allBranches ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          icon={c.isActive ? <CheckCircleIcon /> : <CancelIcon />}
                          label={c.isActive ? 'نشط' : 'غير نشط'}
                          size="small"
                          color={c.isActive ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="تعديل">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => navigate(`/edit-category/${c.id}`)}
                              sx={{
                                border: '1px solid',
                                borderColor: 'primary.main',
                                '&:hover': { bgcolor: 'primary.light' }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="حذف">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(c.id)}
                              sx={{
                                border: '1px solid',
                                borderColor: 'error.main',
                                '&:hover': { bgcolor: 'error.light' }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  );
}